/**
 * arbi tags                        — list tags in workspace
 * arbi tags create [name]          — create a tag (form if no name)
 * arbi tags delete [id]            — delete a tag (picker if no ID)
 * arbi tags update [id] [json]     — update a tag (picker + form if no args)
 */

import { Command } from 'commander'
import { tags as tagOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveWorkspace,
  printTable,
  printJson,
  parseJsonArg,
  dryRun,
} from '../helpers.js'
import { promptSelect, promptInput, promptConfirm } from '../prompts.js'
import { success, ref } from '../output.js'

/** Fetch workspace tags and return as picker choices */
async function fetchTagChoices(arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi']) {
  const data = await tagOps.listTags(arbi)
  if (data.length === 0) {
    process.stderr.write('No tags found.\n')
    process.exit(0)
  }
  return {
    data,
    choices: data.map((t) => ({
      name: `${t.name} (${(t.tag_type as { type?: string })?.type ?? 'unknown'}, ${t.doctag_count} docs)`,
      value: t.external_id as string,
      description: t.external_id as string,
    })),
  }
}

export function registerTagsCommand(program: Command): void {
  const tags = program
    .command('tags')
    .description('Tags: list, create, delete, update, share, unshare, rename')

  tags
    .command('list')
    .description('List tags in the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--json', 'Output as JSON')
    .action((opts: { workspace?: string; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await tagOps.listTags(arbi)

        if (opts.json) {
          // Use the shared printer so every --json command emits the same
          // shape (trailing newline, no chalk leakage).
          printJson(data)
          return
        }

        if (data.length === 0) {
          // Empty-state text goes to stderr — keeps stdout `[]`-shaped-or-empty
          // so `arbi tags list | wc -l` is honest.
          process.stderr.write('No tags found.\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 24, value: (r) => r.external_id as string },
            { header: 'NAME', width: 24, value: (r) => r.name as string },
            {
              header: 'TYPE',
              width: 12,
              value: (r) => (r.tag_type as { type?: string })?.type ?? '',
            },
            { header: 'DOCS', width: 6, value: (r) => String(r.doctag_count) },
            { header: 'SHARED', width: 8, value: (r) => (r.shared ? 'yes' : 'no') },
          ],
          data
        )
      })()
    )

  tags
    .command('create [name]')
    .description('Create a new tag (interactive form if no name given)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option(
      '-t, --type <type>',
      'Tag type (checkbox, text, number, select, folder, search, date, assistant)',
      'checkbox'
    )
    .option('-i, --instruction <text>', 'Tag instruction')
    .option('--shared', 'Make tag shared', false)
    .action(
      (
        name: string | undefined,
        opts: {
          workspace?: string
          type?: string
          instruction?: string
          shared?: boolean
        }
      ) =>
        runAction(async () => {
          const { arbi } = await resolveWorkspace(opts.workspace)

          // Interactive form if name not given
          const interactive = !name
          if (!name) {
            name = await promptInput('Tag name')
          }
          const tagType =
            opts.type ||
            (await promptSelect('Tag type', [
              { name: 'Checkbox', value: 'checkbox' },
              { name: 'Text', value: 'text' },
              { name: 'Number', value: 'number' },
              { name: 'Select', value: 'select' },
              { name: 'Date', value: 'date' },
              { name: 'Folder', value: 'folder' },
              { name: 'Search', value: 'search' },
              { name: 'Assistant', value: 'assistant' },
            ]))
          const instruction = interactive
            ? (opts.instruction ?? ((await promptInput('Instruction (optional)', false)) || null))
            : (opts.instruction ?? null)
          const shared = interactive
            ? opts.shared || (await promptConfirm('Shared?', false))
            : (opts.shared ?? false)

          const data = await tagOps.createTag(arbi, {
            name,
            tagType: {
              type: tagType as 'checkbox' | 'text' | 'number' | 'select' | 'search' | 'date',
              options: [],
            },
            instruction,
            shared,
          })
          // Agents extract the new ID from the predictable text:
          //   arbi tags create … | grep -oE 'tag-[a-z0-9]+'
          success(`Created: ${data.name} (${ref(data.external_id)})`)
        })()
    )

  tags
    .command('delete [id]')
    .description('Delete a tag (interactive picker if no ID given)')
    .option('--dry-run', 'Preview which tag would be deleted (no SDK call)')
    .action((id: string | undefined, opts: { dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        if (!id) {
          const { choices } = await fetchTagChoices(arbi)
          id = await promptSelect('Select tag to delete', choices)
        }
        if (opts?.dryRun) {
          dryRun('delete tag', id)
          return
        }
        const data = await tagOps.deleteTag(arbi, id)
        success(data?.detail ?? `Deleted tag ${id}`)
      })()
    )

  // ── share / unshare / rename ─────────────────────────────────────────────
  // Named shortcuts for the most common `tags update` operations — agents
  // shouldn't have to hand-write {"shared":true} JSON for a one-flag toggle.
  tags
    .command('share <id>')
    .description('Mark a tag as shared (visible to other workspace members)')
    .action((id: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await tagOps.updateTag(arbi, id, { shared: true })
        success(`Shared: ${data.name} (${ref(data.external_id)})`)
      })()
    )
  tags
    .command('unshare <id>')
    .description('Mark a tag as private (owner-only)')
    .action((id: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await tagOps.updateTag(arbi, id, { shared: false })
        success(`Unshared: ${data.name} (${ref(data.external_id)})`)
      })()
    )
  tags
    .command('rename <id> <name>')
    .description('Rename a tag')
    .action((id: string, name: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await tagOps.updateTag(arbi, id, { name })
        success(`Renamed: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  tags
    .command('update [id] [json]')
    .description('Update a tag (interactive picker + form if no args)')
    .action((id?: string, json?: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        if (!id) {
          const { choices } = await fetchTagChoices(arbi)
          id = await promptSelect('Select tag to update', choices)
        }

        let body: Record<string, unknown>
        if (json) {
          body = parseJsonArg(json, 'arbi tags update tag-123 \'{"name": "New Name"}\'')
        } else {
          const field = await promptSelect('Field to update', [
            { name: 'Name', value: 'name' },
            { name: 'Instruction', value: 'instruction' },
            { name: 'Shared', value: 'shared' },
          ])
          if (field === 'shared') {
            body = { shared: await promptConfirm('Shared?') }
          } else {
            body = { [field]: await promptInput(field.charAt(0).toUpperCase() + field.slice(1)) }
          }
        }

        const data = await tagOps.updateTag(arbi, id, body)
        success(`Updated: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  // Default action: list. We do NOT pre-declare --json/-w on the parent
  // because that breaks `arbi tags list --json` — commander binds the option
  // to the parent and the inner `list` subcommand's --json never fires.
  // `allowUnknownOption` keeps `arbi tags --json` working by routing those
  // flags into `tags.args` for forwarding to the inner subcommand.
  tags
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = tags.args ?? []
      await tags.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
